import React from "react";
import { Link } from "react-router-dom";
import { HOME } from "@/constants/testIds";
import { Flame, Dumbbell, UtensilsCrossed, BrainCircuit, LineChart, Target } from "lucide-react";
import LandingContact from "@/components/LandingContact";

const HERO_IMG = "https://images.pexels.com/photos/35540076/pexels-photo-35540076.jpeg";
const MEAL_IMG = "https://images.pexels.com/photos/4929671/pexels-photo-4929671.jpeg";
const COACH_IMG = "https://images.pexels.com/photos/32695885/pexels-photo-32695885.jpeg";

export default function Landing() {
  return (
    <div className="min-h-screen bg-obsidian-900 text-white">
      {/* Nav */}
      <header className="sticky top-0 z-30 border-b border-obsidian-600 bg-obsidian-900/95 backdrop-blur">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/icon-192.png" alt="Ripple Effect" className="h-7 w-7 rounded-sm" />
            <span className="font-heading text-2xl tracking-tight">RIPPLE<span className="text-blaze"> EFFECT</span></span>
          </Link>
          <nav className="flex items-center gap-3">
            <Link to="/contact" data-testid="landing-contact-link" className="ghost-btn px-4 py-2 text-xs hidden sm:inline-flex">Contact</Link>
            <Link to="/auth/login" data-testid={HOME.ctaLogin} className="ghost-btn px-4 py-2 text-xs">Log in</Link>
            <Link to="/auth/signup" data-testid={HOME.ctaStart} className="blaze-btn px-4 py-2 text-xs">Get started</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-obsidian-900 via-obsidian-900/85 to-obsidian-900/40" />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8 py-24 md:py-36 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 animate-fade-up">
            <div className="overline text-blaze mb-4">Performance · AI · Real Results</div>
            <h1 className="font-heading uppercase text-5xl sm:text-6xl md:text-7xl leading-[0.95] tracking-tighter">
              Train smarter.<br />
              <span className="text-blaze">Eat sharper.</span><br />
              Progress faster.
            </h1>
            <p className="mt-6 max-w-xl text-zinc-300 text-base md:text-lg leading-relaxed">
              Ripple Effect is your personal coach, dietitian and progression engine — built around your goals,
              calibrated to your data, powered by Claude Sonnet 4.6.
            </p>
            <div className="mt-8 flex items-center gap-4">
              <Link to="/auth/signup" className="blaze-btn px-7 py-3 text-sm">Start free</Link>
              <Link to="/auth/login" className="ghost-btn px-7 py-3 text-sm">I have an account</Link>
            </div>
          </div>
          <div className="md:col-span-5 hidden md:grid grid-cols-2 gap-4">
            {[
              { k: "BMR/TDEE", v: "Auto-calibrated" },
              { k: "Progression", v: "Per-exercise AI" },
              { k: "Meal plans", v: "Macro-matched" },
              { k: "Coach", v: "24/7 chat" },
            ].map((s) => (
              <div key={s.k} className="obs-card animate-fade-up">
                <div className="overline mb-2">{s.k}</div>
                <div className="font-heading text-2xl">{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features bento */}
      <section className="max-w-7xl mx-auto px-5 md:px-8 py-20">
        <div className="overline text-blaze">/ The system</div>
        <h2 className="font-heading text-4xl md:text-5xl mt-2 mb-12 uppercase">Everything to engineer your physique.</h2>

        <div className="grid grid-cols-1 md:grid-cols-6 gap-6">
          <div className="md:col-span-4 obs-card relative overflow-hidden min-h-[280px]">
            <img src={MEAL_IMG} alt="meal prep" className="absolute inset-0 w-full h-full object-cover opacity-30" />
            <div className="absolute inset-0 bg-gradient-to-tr from-obsidian-900 via-obsidian-900/70 to-transparent" />
            <div className="relative">
              <UtensilsCrossed className="h-7 w-7 text-blaze mb-4" />
              <div className="overline mb-2">AI Meal Planning</div>
              <h3 className="font-heading text-3xl uppercase mb-3">Macro-locked meal plans.</h3>
              <p className="text-zinc-300 max-w-md">High-protein recipes, weekly grocery lists, and one-tap food swaps that hold your macros within ±10%.</p>
            </div>
          </div>
          <div className="md:col-span-2 obs-card">
            <BrainCircuit className="h-7 w-7 text-blaze mb-4" />
            <div className="overline mb-2">AI Macro Calculator</div>
            <h3 className="font-heading text-3xl uppercase mb-3">BMR · TDEE · Targets.</h3>
            <p className="text-zinc-400 text-sm">Mifflin-St Jeor + goal-tuned macros. Updates as your weight or goal shifts.</p>
          </div>

          <div className="md:col-span-2 obs-card">
            <Dumbbell className="h-7 w-7 text-blaze mb-4" />
            <div className="overline mb-2">Workout tracker</div>
            <h3 className="font-heading text-3xl uppercase mb-3">Log sets. Hit PRs.</h3>
            <p className="text-zinc-400 text-sm">Sets, reps, RPE, rest, notes — auto-volume math and PR detection.</p>
          </div>
          <div className="md:col-span-2 obs-card">
            <Target className="h-7 w-7 text-blaze mb-4" />
            <div className="overline mb-2">Progression engine</div>
            <h3 className="font-heading text-3xl uppercase mb-3">Smart overload.</h3>
            <p className="text-zinc-400 text-sm">AI reads recent sessions and suggests weight, rep, or set increases — or a deload when you need it.</p>
          </div>
          <div className="md:col-span-2 obs-card relative overflow-hidden">
            <img src={COACH_IMG} alt="coach" className="absolute inset-0 w-full h-full object-cover opacity-25" />
            <div className="absolute inset-0 bg-gradient-to-tr from-obsidian-900 via-obsidian-900/70 to-transparent" />
            <div className="relative">
              <LineChart className="h-7 w-7 text-blaze mb-4" />
              <div className="overline mb-2">AI Coach 24/7</div>
              <h3 className="font-heading text-3xl uppercase mb-3">Plateau? Ask.</h3>
              <p className="text-zinc-300 text-sm">Form, recovery, food swaps, motivation — referencing your data.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-obsidian-600">
        <div className="max-w-7xl mx-auto px-5 md:px-8 py-20 grid md:grid-cols-2 gap-10 items-center">
          <div>
            <div className="overline text-blaze mb-3">/ Step into the program</div>
            <h2 className="font-heading text-4xl md:text-5xl uppercase">Start free.<br /> Upgrade when ready.</h2>
            <p className="text-zinc-400 mt-4">10 coach messages free. Premium unlocks unlimited AI coaching, advanced analytics, and custom progression programs.</p>
          </div>
          <div className="flex md:justify-end">
            <Link to="/auth/signup" className="blaze-btn px-8 py-4 text-sm">Create your account</Link>
          </div>
        </div>
      </section>

      <LandingContact />

      <footer className="border-t border-obsidian-600 py-6 text-center text-xs text-zinc-500 uppercase tracking-widest">
        © {new Date().getFullYear()} Ripple Effect · Built with Emergent
      </footer>
    </div>
  );
}
